import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { NavLink } from 'react-router-dom';
import { features, RequireFeatureComponent, featureHelpers } from '../features';
import { integrationTypes, tenantHelpers } from '../tenantInfo';
import { CustomIcon, CustomIconSet } from "@social-solutions/ssg-forms";
import styleable from 'react-styleable';
import defaultStyle from './nav.style.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import * as api from '../api';
import './nav.scss';

/**
 * The side nav
 */
export class ParticipantNavLinks extends Component {
  constructor() {
    super();
    this.state = {
      /** used to identify if and which program is expanded in the nav*/
      expandedProgramId: null,
      permissions: [],
    }
  }

  componentWillMount() {
    if (featureHelpers.featureEnabled(features.apricotPortalTier2) ||
        tenantHelpers.getIntegrationType() !== integrationTypes.apricot) {
      this.loadProgramForms(this.props);
    }
  }

  componentDidMount() {
    api.get('/sessions/permissions', null)
    .then(res => res)
    .then(res => {
      this.setState({permissions: res})
    })
  }

  loadProgramForms(props) {
    const { auth, programEnrollment, programEnrollmentActions } = props;
    const { enrollment } = programEnrollment;
    const { currentSession } = auth;
    const participantId = currentSession && currentSession.user && currentSession.user.id;

    if(!programEnrollment.isFetching && (!enrollment || !enrollment.sites)){
      programEnrollmentActions.fetchEnrolledProgramFormPermissions(participantId);
    }
  }

  showUpdates(isParent, providerId, subjectIdentifier) {
    return (isParent || providerId + '' === subjectIdentifier + '' );
  }

  updatesControl(show, updates) {
    updates = show ? updates : 0;
    return (
      <div className={`pending-updates ${updates ? 'has-pending-updates' : ''}`}>
        <span className="pending-updates-count">
          {updates}
        </span>
        {'Pending Update' + (updates === 1 ? '' : 's')}
      </div>
    );
  }

  renderAppointmentsNavLink(activeClass) {
    return(
      <NavLink
        activeClassName={activeClass}
        className="left-nav-item"
        exact
        id="scheduler-link"
        role='listitem'
        to="/appointments"
        tabIndex={-1}
      >
        <div id="dashboard-image" className="nav-logo-wrapper">
          <FontAwesomeIcon icon={faCalendar} />
        </div>
        <div id="dashboard-label" className="left-nav-item-label">Appointments</div>
      </NavLink>
    );
  }

  /**
   * handles expanding/collapsing a program to show/hide forms
   */
  handleProgramClick(programId) {
    const { expandedProgramId } = this.state;

    if(programId === expandedProgramId) {
      this.setState({
        expandedProgramId: undefined
      })
    } else {
      this.setState({
        expandedProgramId: programId
      })
    }
  }

  /**
   * render a program for the nav. if expanded, will also render the program's forms
   * @param {*} site - site object from programEnrollment, to which the program belongs
   * @param {*} program - program object from programEnrollment
   * @param {*} tabIndex - current tabIndex being used to ensure navigation is logical

   */
  getProgram(site, program, tabIndex) {
    const { expandedProgramId } = this.state;
    var activeClass = "router-link-active";

    const programId = program && program.programId ? program.programId : undefined;

    // if no forms available in program, do not render
    const formsExist = program && program.formPermissions && program.formPermissions.find(form => form.addPerm && form.enabled);
    if (!formsExist) { return null }

    const programItem = programId !== expandedProgramId ?
      <ul
        className="program-nav-zone"
      >
        <div
          className="left-nav-item"
          id={programId + "-program"}
          onClick={() => this.handleProgramClick(programId)}
          onKeyUp={function(e) {
            if (e.key === 'Enter' || e.key === ' ') {
              this.handleProgramClick(programId);
            }
          }}
          tabIndex={tabIndex += Number(0.00001)}
        >
          <div className="arrow-right nav-logo-wrapper">
            <CustomIcon iconChoice={CustomIconSet.ic_right_arrow_14x14} css={{ ssgCustomIconContainer: "left-nav-icon-container", ssgCustomIcon: "left-nav-icon" }} />
          </div>
          <div className="program-title">{!program.alias ? program.name : program.alias}</div>
        </div>
      </ul>
      :
      <ul className="program-nav-zone zone-expanded">
        <div
          className="left-nav-item"
          id={programId + "-program"}
          onClick={() => this.handleProgramClick(programId)}
          onKeyUp={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              this.handleProgramClick(programId);
            }
          }}
          role="list"
          tabIndex={-1}
        >
          <div className="arrow-down nav-logo-wrapper">
            <CustomIcon iconChoice={CustomIconSet.ic_down_arrow_14x14} css={{ ssgCustomIconContainer: "left-nav-icon-container", ssgCustomIcon: "left-nav-icon" }} />
          </div>
          <div className="program-title left-nav-label">{!program.alias ? program.name : program.alias}</div>
        </div>
        <div className="left-nav-forms">
          {program.formPermissions.map(form => !!form && form.addPerm && form.enabled ? (
            <li key={form.touchpointId}>
              <NavLink to={`/sites/${site.siteId}/programs/${program.programId}/forms/${form.touchpointId}/create`}
                className="left-nav-item"
                activeClassName={activeClass}
                tabIndex={tabIndex += Number(0.000001)}
                >
                <div id="left-nav-label">{!form.alias ? form.externalName : form.alias}</div>
              </NavLink>
            </li>
          ) : null)}
        </div>
      </ul>

    return programItem;
  }

  /**
   * render a site for the nav if it has any programs with available forms
   * @param {*} site - site object from programEnrollment in the store
   */
  getSite(site, tabIndex) {
    const { expandedProgramId } = this.state;
    const formsExist = site && site.programs && site.programs.find(program =>
      program.formPermissions && program.formPermissions.find(form => form.addPerm && form.enabled));
    if (!formsExist) { return null }

    const siteElement =
    <div className="site-nav-zone">
      <div role='list' className="site-nav left-nav-item">{ `${site.name}` }</div>
        { site.programs.map(program => (
          <div
            aria-expanded={expandedProgramId === program.programId}
            aria-label={program.name + ' program selector'}
            className="program"
            id={'program-button-' + program.programId}
            key={program.programId}
            onKeyUp={e => (e.key === 'Enter' || e.key === ' ') ? this.handleProgramClick(program.programId) : {}}
            role='button'
            tabIndex={-1}
          >
            { this.getProgram(site, program, tabIndex += Number(0.0001))}
          </div>
        )) }
    </div>

    return siteElement;
  }

  render() {
    const { programEnrollment, tenant } = this.props;
    const { featureList } = tenant? tenant.currentTenant : [];
    const { expandedProgramId, permissions } = this.state;
    let tabIndexValue = Number(2.5);

    var activeClass = "router-link-active";

    const apricotSchedulerEnabled = permissions && permissions.length > 0 ? permissions.filter(p => p.name === 'ConnectScheduler')[0].enabled : false;
    const connectSchedulerEnabled = featureList && featureList.length > 0 ? featureList.filter(p => p.name === 'ConnectScheduler')[0].enabled : false;

    // explicitly show site names only when user belongs to more than 1 site
    const showSites = !!programEnrollment && !!programEnrollment.enrollment
      && !!programEnrollment.enrollment.sites && programEnrollment.enrollment.sites.length > 1;

    var programLists = showSites ? ((programEnrollment && programEnrollment.enrollment && programEnrollment.enrollment.sites) || []).map(site => (
      <ul className="site" key={site.siteId}>
        { this.getSite(site, tabIndexValue) }
      </ul>
    ))
    :
    ((programEnrollment && programEnrollment.enrollment && programEnrollment.enrollment.sites) || []).map(site => (
      <div key={site.siteId}>
        {site.programs.map(program => (
          <div
            aria-expanded={expandedProgramId === program.programId}
            aria-label={program.name + ' program selector'}
            className='program'
            id={'program-button-' + program.programId}
            onKeyUp={e => (e.key === 'Enter' || e.key === ' ') ? this.handleProgramClick(program.programId) : {}}
            key={program.programId}
            role='button'
            tabIndex={tabIndexValue += Number(0.001)}
          >
            {this.getProgram(site, program, tabIndexValue)}
          </div>
        ))}
      </div>
    ));

    if(programEnrollment.isFetching) {
      programLists = <div id="left-nav-loading-msg" className="left-nav-item">Loading Forms...</div>
    }

    return (
      <div role="navigation" className="participant-nav-links">
        <section
          className="left-nav-section"
          id="dashboard-nav-section"
        >
          <ul>
            <li
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.firstChild.click();
                }
              }}
              tabIndex="2.1"
            >
              <NavLink
                activeClassName={activeClass}
                className="left-nav-item"
                exact
                id="dashboard-link"
                to="/"
                tabIndex={-1}
              >
                <div id="dashboard-image" className="nav-logo-wrapper">
                  <CustomIcon
                    css={{
                      ssgCustomIcon: "left-nav-icon",
                      ssgCustomIconContainer: "left-nav-icon-container",
                    }}
                    iconChoice={CustomIconSet.ic_dashboard_16x12}
                  />
                </div>
                <div id="dashboard-label" className="left-nav-item-label">Dashboard</div>
              </NavLink>
            </li>
            <li
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.firstChild.click();
                }
              }}
              tabIndex="2.2"
            >
              {apricotSchedulerEnabled && connectSchedulerEnabled && this.renderAppointmentsNavLink(activeClass)}
            </li>
            <li
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.firstChild.firstChild.click();
                }
              }}
              tabIndex="2.3"
            >
              <RequireFeatureComponent
                isListItem={true}
                requiredFeatures={[features.apricotPortalTier2]}
                requiredForIntegrationTypes={[integrationTypes.apricot]}>
                <NavLink
                  activeClassName={activeClass}
                  className="left-nav-item"
                  exact
                  id="responses-link"
                  to="/responses"
                  tabIndex={-1}
                >
                  <div id="responses-image" className="nav-logo-wrapper">
                    <CustomIcon
                      css={{
                        ssgCustomIcon: "left-nav-icon",
                        ssgCustomIconContainer: "left-nav-icon-container",
                      }}
                      iconChoice={CustomIconSet.ic_form_submit_16x16}
                    />
                  </div>
                  <div id="responses-label" className="left-nav-item-label" >Form Submissions</div>
                </NavLink>
              </RequireFeatureComponent>
            </li>
          </ul>
        </section>
        <section id="programs-nav-section" className="left-nav-section">
          <RequireFeatureComponent
            requiredFeatures={[features.apricotPortalTier2]}
            requiredForIntegrationTypes={[integrationTypes.apricot]}>
            <div className="assigned-forms-container">
              <span id="assigned-forms-label">ALL ASSIGNED FORMS</span>
            </div>
            {programLists}
          </RequireFeatureComponent>
        </section>
      </div>
    );
  }
}

ParticipantNavLinks.propTypes = {
  auth:PropTypes.any.isRequired,
  programEnrollment: PropTypes.any.isRequired,
  programEnrollmentActions: PropTypes.any.isRequired,
  setKey: PropTypes.func,
  permissions: PropTypes.any,
  tenant: PropTypes.any,
};

export default styleable(defaultStyle) (ParticipantNavLinks);



// WEBPACK FOOTER //
// src/layout/participant-nav-links.js